import type { Anchoring, IsoDate, Recurrence } from "./types.ts";

/**
 * "YYYY-MM-DD" vs "YYYY-MM" — whether a date carries a day at all.
 *
 * Shape only, and deliberately: "2026-09-31" is day-GRANULAR and calendar-
 * impossible, and the two are separate questions. A boundary that needs the
 * second one routes the value through `toDayOrdinal`/`monthOf`, which already
 * throw on exactly that.
 */
const DAY_GRANULAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Whether `date` names a specific day, rather than a whole month. */
export function isDayGranularDate(date: IsoDate): boolean {
  return DAY_GRANULAR_DATE.test(date);
}

/**
 * Whether a recurring cost's `startDate` must name a specific day, given the
 * schedule it describes. The ONE statement of this rule: both write
 * boundaries (`store/actions.ts`, `export/json.ts`) and both controls that
 * offer these pairings (`ui/recurringForm.ts`, the Settings recurring table)
 * call this rather than restating it, because four copies of a rule is how a
 * fifth place ends up without one.
 *
 * `everyNMonths` under `calendar` anchoring is the single exception: that grid
 * walks its cursor through `monthOf`, so a month-only date is exactly right —
 * rent occurs IN a month. Every other pairing walks day-precisely:
 * `everyNDays` and `everyNWeeks` hand the date straight to `addDays`
 * regardless of anchoring, and `everyNMonths` under `lastCharge` steps through
 * `addMonthsToDate` because the next due date is measured from a real charge.
 */
export function startDateNeedsDay(kind: Recurrence["kind"], anchoring: Anchoring): boolean {
  return kind !== "everyNMonths" || anchoring === "lastCharge";
}

/**
 * Whether a CHARGE's own `Purchase.date` must name a specific day, given the
 * anchoring of the cost it names.
 *
 * A separate rule from `startDateNeedsDay`, because the two dates are read
 * differently. A `calendar` cost's charge is only ever placed in a month (its
 * slot lives in `source.occurrenceDate`), so a month-only purchase date is
 * fine there whatever the recurrence kind. A `lastCharge` cost has no slots at
 * all: `anchoredOccurrences` takes the LATEST charge's own date as its anchor
 * and steps forward from it, so that date reaches `addDays`/`addMonthsToDate`
 * under every recurrence kind and must carry a day.
 */
export function chargeDateNeedsDay(anchoring: Anchoring): boolean {
  return anchoring === "lastCharge";
}
